// src/components/DashboardAdmin/ICTData/TravelData.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTravelData,
  insertTravelData,
  updateTravelData,
  deleteTravelData,
} from "../../../store/travelDataSlice";
import { fetchICTData } from "../../../store/uiSlice";
import { fetchEmployees } from "../../../store/empDataSlice";
import { fetchTrainingCards } from "../../../store/trainingCardsSlice";
import "../../../Styles/Common.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TravelData = () => {
  const dispatch = useDispatch();

  // Redux state
  const { travelData, loading, error } = useSelector((state) => state.travelData);
  const { ictData } = useSelector((state) => state.ui);
  const { employees } = useSelector((state) => state.empData);
  const { trainingCards } = useSelector((state) => state.trainingCards);

  // Local state for search, pagination, and form popup.
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form state for insert/edit.
  // "id" is auto-generated on insert so it should be blank.
  const [formData, setFormData] = useState({
    id: "",
    travelType: "Car",
    travelDesc: "",
    travelSDate: "",
    travelSTime: "",
    travelEDate: "",
    travelETime: "",
    travellerName: "",
    pnr: "",
    tc_id: "",
    travelCost: "",
    travelBill: null, // Will hold a File object.
    status: "A",
  });

  // Live search state for TC Id.
  const [tcSearchInput, setTcSearchInput] = useState("");
  const [tcSuggestions, setTcSuggestions] = useState([]);

  const itemsPerPage = 10;

  // Fetch initial data.
  useEffect(() => {
    dispatch(fetchTravelData());
    dispatch(fetchICTData());
    dispatch(fetchEmployees());
    dispatch(fetchTrainingCards());
  }, [dispatch]);

  // --- Live search for TC Id (min 1 character) ---
  const handleTcSearchInputChange = (e) => {
    const value = e.target.value;
    setTcSearchInput(value);
    if (value.length >= 1) {
      const suggestions = trainingCards.filter((tc) =>
        String(tc.id).includes(value)
      );
      setTcSuggestions(suggestions);
    } else {
      setTcSuggestions([]);
    }
  };

  const handleSelectTcSuggestion = (tc) => {
    setFormData({ ...formData, tc_id: tc.id });
    setTcSearchInput(String(tc.id));
    setTcSuggestions([]);
  };

  // --- General form input change handler ---
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // File upload handler for Travel Bill.
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData({ ...formData, travelBill: e.target.files[0] });
    }
  };

  // Helper to process formData.
  // In update mode, include the id; otherwise, remove it.
  const processFormData = () => {
    if (editMode) {
      return {
        ...formData,
        tc_id: formData.tc_id ? Number(formData.tc_id) : 0,
        travelCost: formData.travelCost ? Number(formData.travelCost) : 0,
      };
    } else {
      const { id, ...rest } = formData;
      return {
        ...rest,
        tc_id: rest.tc_id ? Number(rest.tc_id) : 0,
        travelCost: rest.travelCost ? Number(rest.travelCost) : 0,
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const processedData = processFormData();
    console.log("Data to be sent:", processedData);
    try {
      if (editMode) {
        await dispatch(updateTravelData(processedData)).unwrap();
        toast.success("Travel data updated successfully!");
      } else {
        await dispatch(insertTravelData(processedData)).unwrap();
        toast.success("Travel data added successfully!");
      }
      setIsFormOpen(false);
      setFormData({
        id: "",
        travelType: "Car",
        travelDesc: "",
        travelSDate: "",
        travelSTime: "",
        travelEDate: "",
        travelETime: "",
        travellerName: "",
        pnr: "",
        tc_id: "",
        travelCost: "",
        travelBill: null,
        status: "A",
      });
      setTcSearchInput("");
      dispatch(fetchTravelData());
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Failed to save travel data.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this travel record?")) {
      try {
        await dispatch(deleteTravelData(id)).unwrap();
        toast.success("Travel record deleted successfully!");
        dispatch(fetchTravelData());
      } catch (err) {
        toast.error("Failed to delete travel data.");
      }
    }
  };

  // Filter travel records by travellerName.
  const filteredTravelData = travelData.filter((td) =>
    td.travellerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredTravelData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTravelData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="container">
      <div className="header-container">
        <h2>Travel Data</h2>
        <button
          className="add-lead-btn"
          onClick={() => {
            setIsFormOpen(true);
            setEditMode(false);
            setFormData({
              id: "",
              travelType: "Car",
              travelDesc: "",
              travelSDate: "",
              travelSTime: "",
              travelEDate: "",
              travelETime: "",
              travellerName: "",
              pnr: "",
              tc_id: "",
              travelCost: "",
              travelBill: null,
              status: "A",
            });
            setTcSearchInput("");
          }}
        >
          <i className="fa fa-plus"></i>
        </button>
      </div>
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search by Traveller Name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
      </div>
      {loading && <p>Loading travel data...</p>}
      {error && (
        <p className="error">
          {typeof error === "object" ? JSON.stringify(error) : error}
        </p>
      )}
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Type</th>
            <th>Description</th>
            <th>Start Date</th>
            <th>Start Time</th>
            <th>End Date</th>
            <th>End Time</th>
            <th>Traveller Name</th>
            <th>PNR</th>
            <th>TC Id</th>
            <th>Cost</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((td, index) => (
            <tr key={td.id}>
              <td>{index + 1}</td>
              <td>{td.travelType}</td>
              <td>
                {td.travelBill ? (
                  <a
                    href={`https://iteyonikservicesinstitute.com/download/documents/travels/${td.travelBill.split("/").pop()}`}
                    download
                  >
                    {td.travelDesc}
                  </a>
                ) : (
                  td.travelDesc
                )}
              </td>
              <td>{td.travelSDate}</td>
              <td>{td.travelSTime}</td>
              <td>{td.travelEDate}</td>
              <td>{td.travelETime}</td>
              <td>{td.travellerName}</td>
              <td>{td.pnr}</td>
              <td>{td.tc_id}</td>
              <td>
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                }).format(td.travelCost)}
              </td>
              <td>
                <button
                  onClick={() => handleDelete(td.id)}
                  className="icon-btn delete-btn"
                >
                  <i className="fa fa-trash" aria-hidden="true"></i>
                </button>
                <button
                  className="icon-btn update-btn"
                  onClick={() => {
                    setFormData({
                      id: td.id,
                      travelType: td.travelType,
                      travelDesc: td.travelDesc,
                      travelSDate: td.travelSDate?.split("T")[0] || "",
                      travelSTime: td.travelSTime,
                      travelEDate: td.travelEDate?.split("T")[0] || "",
                      travelETime: td.travelETime,
                      travellerName: td.travellerName,
                      pnr: td.pnr,
                      tc_id: td.tc_id,
                      travelCost: td.travelCost,
                      // Do not auto-fill file for update.
                      travelBill: null,
                      status: td.status,
                    });
                    setTcSearchInput(String(td.tc_id));
                    setEditMode(true);
                    setIsFormOpen(true);
                  }}
                >
                  <i className="fa fa-edit" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      {/* Insert/Edit Form Popup */}
      {isFormOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit Travel Data" : "Add Travel Data"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Travel Type</label>
                <select
                  name="travelType"
                  value={formData.travelType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Car">Car</option>
                  <option value="Bus">Bus</option>
                  <option value="Train">Train</option>
                  <option value="Flight">Flight</option>
                  <option value="Others">Others</option>
                </select>
              </div>
              <div className="form-group">
                <label>Travel Description</label>
                <textarea
                  name="travelDesc"
                  placeholder="Enter travel description"
                  value={formData.travelDesc}
                  onChange={handleInputChange}
                ></textarea>
              </div>
              <div className="doubleFormField">
                <div className="form-group halfForm">
                  <label>Travel Start Date</label>
                  <input
                    type="date"
                    name="travelSDate"
                    value={formData.travelSDate || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group halfForm">
                  <label>Travel Start Time</label>
                  <input
                    type="time"
                    name="travelSTime"
                    value={formData.travelSTime || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="doubleFormField">
                <div className="form-group halfForm">
                  <label>Travel End Date</label>
                  <input
                    type="date"
                    name="travelEDate"
                    value={formData.travelEDate || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group halfForm">
                  <label>Travel End Time</label>
                  <input
                    type="time"
                    name="travelETime"
                    value={formData.travelETime || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="doubleFormField">
                <div className="form-group halfForm">
                    <label>PNR</label>
                    <input
                    type="text"
                    name="pnr"
                    placeholder="Enter PNR"
                    value={formData.pnr}
                    onChange={handleInputChange}
                    />
                </div>
                {/* Live search for TC Id */}
                <div className="form-group halfForm">
                    <label>TC Id</label>
                    <input
                    type="text"
                    placeholder="Search TC Id"
                    value={tcSearchInput}
                    onChange={handleTcSearchInputChange}
                    required
                    />
                    {tcSuggestions.length > 0 && (
                    <div className="suggestions-dropdown">
                        {tcSuggestions.map((tc) => (
                        <div
                            key={tc.id}
                            className="suggestion-item"
                            onClick={() => handleSelectTcSuggestion(tc)}
                        >
                            {tc.id}
                        </div>
                        ))}
                    </div>
                    )}
                </div>
              </div>
              <div className="form-group">
                <label>Traveller Name</label>
                <input
                  type="text"
                  name="travellerName"
                  placeholder="Enter traveller name"
                  value={formData.travellerName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Travel Cost</label>
                <input
                  type="number"
                  step="0.01"
                  name="travelCost"
                  placeholder="Enter Amount (INR)"
                  value={formData.travelCost}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {/* File upload for Travel Bill */}
              <div className="form-group">
                <label>Travel Bill</label>
                <input
                  type="file"
                  name="travelBill"
                  accept=".pdf,.jpg,.jpeg,.doc,.docx"
                  onChange={handleFileChange}
                  required={!editMode}
                />
                {editMode && (
                  <p style={{ fontSize: "0.9em" }}>
                    If you don't select a new file, the old file remains.
                  </p>
                )}
              </div>
              <div className="form-actions">
                <button type="submit">
                  {editMode ? (
                    <i className="fa fa-pencil" aria-hidden="true"></i>
                  ) : (
                    <i className="fa fa-plus-square" aria-hidden="true"></i>
                  )}
                </button>
                <button
                  type="button"
                  className="fa fa-close"
                  onClick={() => setIsFormOpen(false)}
                >
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default TravelData;
